import React from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import { ClassNameMap } from '@mui/styles/withStyles';

import { Grid, GridSize } from '@mui/material';

import ColumnStepper from './ColumnStepper';

import styles from './styles';

interface Props extends WithStyles<typeof styles> {
  classes: ClassNameMap<string>;
  completeLabel: string;
  isNextDisabled: boolean;
  gridSize?: GridSize;
  progressGridSize?: GridSize;
  children: CustomStepperStep[];
  step: number;
  subStep?: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  validate: (stepIndex: number, subStepIndex?: number) => boolean;
  handleComplete: () => void;
  setSubStep?: React.Dispatch<React.SetStateAction<number>>;
}

interface SubStep {
  label: string;
  component: React.ReactElement;
  detail?: string | null;
}

interface CustomStepperStep {
  label: string;
  detail?: string | null;
  gridSize?: GridSize;
  nextLabel?: string;
  handleNext?: () => void;
  component?: React.ReactElement;
  subSteps?: SubStep[];
}
const CustomStepper = ({
  classes,
  children = [],
  completeLabel,
  isNextDisabled,
  gridSize = 4,
  progressGridSize = 4,
  step,
  subStep,
  setStep,
  setSubStep,
  validate,
  handleComplete,
}: Props): React.ReactElement => {
  const currentStep = children[step];
  const hasSubSteps = currentStep.subSteps?.length;

  if (hasSubSteps && subStep === undefined) {
    throw new Error('subStep is required when subSteps are defined');
  }

  const handleNextStep = () => {
    if (hasSubSteps && subStep! + 1 < currentStep.subSteps!.length) {
      if (!validate(step, subStep)) {
        setSubStep?.(subStep! + 1);
      }
    } else {
      if (!validate(step, subStep)) {
        setStep(step + 1);
        setSubStep?.(0);
      }
    }
  };

  const handlePreviousStep = () => {
    if (hasSubSteps && subStep! > 0) {
      setSubStep?.(subStep! - 1);
    } else if (step > 0) {
      const previousStep = children[step - 1];
      const previousSubSteps = previousStep.subSteps?.length || 0;
      setStep(step - 1);
      setSubStep?.(previousSubSteps > 0 ? previousSubSteps - 1 : 0);
    }
  };

  return (
    <Grid container justifyContent={'space-between'} spacing={3}>
      <Grid item xs={currentStep.gridSize || gridSize}>
        <Grid container>
          <Grid item xs={12}>
            {hasSubSteps ? currentStep.subSteps?.[subStep!]?.component : currentStep.component}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={progressGridSize}>
        <ColumnStepper
          title="Progress"
          step={step}
          subStep={subStep}
          steps={children.map((s) => ({
            label: s.label,
            detail: s.detail,
            nextLabel: s.nextLabel,
            handleNext: s.handleNext,
            subSteps: s.subSteps?.map((sub) => ({ ...sub })) || [],
          }))}
          completeLabel={completeLabel}
          isNextDisabled={isNextDisabled}
          handleStepClick={(index) => {
            setStep(index);
            setSubStep?.(0);
          }}
          handleSubStepClick={(index, subIndex) => {
            setStep(index);
            setSubStep?.(subIndex);
          }}
          handlePrevious={handlePreviousStep}
          handleNext={handleNextStep}
          handleComplete={handleComplete}
        />
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(CustomStepper);
